import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import {
  type GqlIdentity,
  type GqlMeeting,
  type GqlMeetingConcern,
  type GqlOptionsRegistration,
  type GqlWarrantParticipant,
  type GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../../../graphql/base-types.graphql';
import { OptionsRegistrationForm } from '../../../../../common/components/documents/OptionsRegistrationForm';
import { ConvertiblesRegistrationForm } from '../../../../../common/components/documents/ConvertiblesRegistrationForm';
import { WarrantAgreementDocumentsList } from '../../../../../common/components/documents/WarrantAgreementDocumentsList';
import { type DisplayDocument } from '@startuptools/business-ui/document/Document';
import { type DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { MeetingMinutesDocument } from '../../../../../common/components/documents/MeetingMinutes';

export const OptionsRegistrationDocuments = ({
  optionsRegistration,
  navigateToDecisionStep,
}: {
  optionsRegistration: Pick<GqlOptionsRegistration, 'warrantProgramId' | 'description' | 'id'> & {
    latestRegistrationForm?: DisplayDocument | null;
    warrantProgram: Pick<
      GqlWarrantProgram,
      'id' | 'name' | 'warrantKind' | 'usesStepper' | 'paidForAt' | 'companyId'
    > & {
      meetingConcern?:
        | (Pick<GqlMeetingConcern, 'id'> & {
            meeting: Pick<GqlMeeting, 'id' | 'number' | 'kind' | 'startTime' | 'usesStepper'>;
          })
        | null;
    };
    participants: (Pick<GqlWarrantParticipant, 'id' | 'optionsRegistrationId'> & {
      identity: DisplayIdentity & Pick<GqlIdentity, 'juridicalKind'>;
      latestAgreementDoc?: DisplayDocument | undefined | null;
    })[];
  };
  navigateToDecisionStep: () => void;
}) => {
  return (
    <Card>
      <Stack gap={2}>
        <Stack>
          <Typography level="h3">{optionsRegistration.description}</Typography>
          <Typography>
            <Trans id="Options Subscription" />
          </Typography>
        </Stack>

        <Stack gap={3}>
          {optionsRegistration.warrantProgram.meetingConcern?.meeting && (
            <MeetingMinutesDocument meeting={optionsRegistration.warrantProgram.meetingConcern.meeting} />
          )}
          {[GqlWarrantProgramKind.Warrant, GqlWarrantProgramKind.Wise].includes(
            optionsRegistration.warrantProgram.warrantKind,
          ) && (
            <OptionsRegistrationForm
              warrantProgramId={optionsRegistration.warrantProgramId}
              optionsRegistration={optionsRegistration}
              navigateToDecisionStep={navigateToDecisionStep}
            />
          )}
          {optionsRegistration.warrantProgram.warrantKind === GqlWarrantProgramKind.Nice && (
            <ConvertiblesRegistrationForm
              warrantProgramId={optionsRegistration.warrantProgramId}
              optionsRegistration={optionsRegistration}
              navigateToDecisionStep={navigateToDecisionStep}
            />
          )}
          <WarrantAgreementDocumentsList
            warrantProgram={optionsRegistration.warrantProgram}
            optionsRegistration={optionsRegistration}
            participants={optionsRegistration.participants}
            navigateToDecisionStep={navigateToDecisionStep}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
